import type { Analytics } from "../analytics";
import { Provider } from "../provider";
import type { ProviderListeners } from "../provider-listeners";

type IConfig = {
  collector: string;
  appId: string;
  schemaVendor: string;
};

export class Snowplow extends Provider {
  public key = "snowplow";
  protected analytics: Analytics | undefined;
  private config: IConfig;

  protected listeners: ProviderListeners = {
    setSuperProperties: async (payload) => {
      // Use a generic data schema with typed context
      await this.addGlobalContext(
        "business",
        `iglu:${this.config.schemaVendor}/generic_data/jsonschema/1-0-1`,
        {
          context_type: "business",
          account_id: payload.businessId,
          data: JSON.stringify({
            businessId: payload.businessId,
            businessName: payload.businessName,
            outletId: payload.outletId,
            outletName: payload.outletName,
            orderingMode: payload.orderingMode,
            salesChannel: payload.salesChannel,
          }),
        },
      );

      return payload;
    },

    userAnonymize: async (payload) => {
      // Use a generic data schema with typed context
      await this.addGlobalContext(
        "user",
        `iglu:${this.config.schemaVendor}/generic_data/jsonschema/1-0-1`,
        {
          context_type: "user",
          account_id: this.analytics?.store.superProperties.businessId ?? "",
          data: JSON.stringify({
            id: "",
            email: "",
          }),
        },
      );

      return payload;
    },

    userIdentify: async (payload) => {
      // Use a generic data schema with typed context
      await this.addGlobalContext(
        "user",
        `iglu:${this.config.schemaVendor}/generic_data/jsonschema/1-0-1`,
        {
          context_type: "user",
          account_id: this.analytics?.store.superProperties.businessId ?? "",
          data: JSON.stringify({
            id: payload.id,
            email: payload.email ?? "",
          }),
        },
      );

      return payload;
    },

    pageView: async () => {
      if (typeof window === "undefined") return {};

      const { trackPageView } = await import("@snowplow/browser-tracker");
      trackPageView();
      return {};
    },

    startJourney: async (payload) => {
      await this.trackCustomEvent("journey", "Started Journey", {});
      return payload;
    },

    viewMenu: async (payload) => {
      await this.trackCustomEvent("menu", "Viewed Menu", {
        menuId: payload.id,
        menuSlug: payload.slug,
      });
      return payload;
    },

    orderSuccess: async (payload) => {
      await this.trackCustomEvent("order", "Placed Order", {
        orderUid: payload.orderUid,
      });
      return payload;
    },

    orderFailure: async (payload) => {
      await this.trackCustomEvent("order", "Place Order Failed", {
        orderUid: payload.orderUid,
        reason: payload.reason ?? "Unknown",
      });
      return payload;
    },
  };

  constructor(config: IConfig) {
    super();
    this.config = config;
  }

  protected async onInstall() {
    // Only run in browser (not during SSR or tests)
    if (typeof window === "undefined") return;

    if (!this.config.collector) {
      throw new Error("No Snowplow token provided");
    }

    const { newTracker } = await import("@snowplow/browser-tracker");

    newTracker("sp", this.config.collector, {
      appId: this.config.appId,
      platform: "web",
      cookieDomain: "auto",
      discoverRootDomain: true,
      cookieSameSite: "None",
      contexts: {
        webPage: true,
      },
    });
  }

  protected async trackCustomEvent(
    context: string,
    action: string,
    payload: Record<string, any>,
  ) {
    const { trackSelfDescribingEvent } = await import(
      "@snowplow/browser-tracker"
    );

    trackSelfDescribingEvent({
      event: {
        schema: `iglu:${this.config.schemaVendor}/generic_data/jsonschema/1-0-1`,
        data: {
          context_type: context,
          account_id: this.analytics?.store.superProperties.businessId ?? "",
          data: JSON.stringify({
            action,
            ...payload,
          }),
        },
      },
    });
  }

  protected async addGlobalContext(
    contextKey: string,
    schema: string,
    payload: Record<string, any>,
  ) {
    const { addGlobalContexts } = await import("@snowplow/browser-tracker");
    const contextEntity = {
      schema,
      data: payload,
    };
    addGlobalContexts({ [contextKey]: contextEntity });
  }
}
